/**
 * IMPORTS
 */

import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { Receipt } from "lucide-react";

import { Badge } from "@components/badge/badge";

import { useExtratoMovimentacoesCreditoCliente } from "@/hooks/extrato-credito-cliente/buscar-extrato-por-cliente";
import { useBuscarPorUsuario } from "@/hooks/usuários/buscar-por-um-cliente";

import { DynamicTable } from "@/presentation/components/table-custom/table";

import {
  IExtratoMovimentacaoCreditoClienteLinha,
  TipoOperacaoCreditoClienteCodigo,
} from "@/interface/extrato-credito-cliente";

import { BarraFiltrosExtratoCredito } from "./components/barra-filtros-extrato-credito";
import { ROTULO_POR_TIPO_OPERACAO_EXTRATO } from "./constants";
import { ILinhaExtratoTabelaCliente } from "./interface";

function obterRotuloTipoOperacaoExtrato(codigoTipoOperacao: string): string {
  return (
    ROTULO_POR_TIPO_OPERACAO_EXTRATO[codigoTipoOperacao as TipoOperacaoCreditoClienteCodigo] ??
    codigoTipoOperacao
  );
}

function formatarDataHoraExtratoCredito(isoData: string): string {
  try {
    return new Date(isoData).toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return isoData;
  }
}

function formatarQuantidadeCreditoExtrato(quantidade: number | string): string {
  const valorNumerico = Number(quantidade);
  if (Number.isNaN(valorNumerico)) {
    return String(quantidade);
  }

  return Number.isInteger(valorNumerico) ? String(valorNumerico) : valorNumerico.toFixed(2);
}

function gerarPaginasNavegacao(currentPage: number, lastPage: number): Array<number | string> {
  if (lastPage <= 1) {
    return [1];
  }

  const paginas: Array<number | string> = [];
  paginas.push(1);

  const inicioJanela = Math.max(2, currentPage - 1);
  const fimJanela = Math.min(lastPage - 1, currentPage + 1);

  if (inicioJanela > 2) {
    paginas.push("...");
  }

  for (let pagina = inicioJanela; pagina <= fimJanela; pagina += 1) {
    paginas.push(pagina);
  }

  if (fimJanela < lastPage - 1) {
    paginas.push("...");
  }

  paginas.push(lastPage);

  return paginas;
}

function mapearLinhaExtratoParaTabela(
  linhaExtrato: IExtratoMovimentacaoCreditoClienteLinha
): ILinhaExtratoTabelaCliente {
  return {
    id: linhaExtrato.id,
    dataHoraMovimentacao: formatarDataHoraExtratoCredito(linhaExtrato.created_at),
    tipoOperacaoDescricao: obterRotuloTipoOperacaoExtrato(String(linhaExtrato.tipo_operacao)),
    direcaoMovimentoDescricao: String(linhaExtrato.direcao_movimento),
    quantidadeCreditosFormatada: formatarQuantidadeCreditoExtrato(linhaExtrato.quantidade_creditos),
    saldoAposMovimento: String(linhaExtrato.saldo_creditos_produto_apos_movimento),
    nomeProduto: linhaExtrato.produto?.nome ?? "-",
    numeroRemessa: linhaExtrato.remessa?.numero_remessa
      ? `#${linhaExtrato.remessa.numero_remessa}`
      : "-",
    observacaoNegocio: linhaExtrato.observacao_negocio ?? "-",
  };
}

function ExtratoMovimentacoesCreditoCliente() {
  const location = useLocation();
  const { cliente } = location.state || {};
  const clienteIdParaExtrato = cliente?.id as number | undefined;

  const { user: clienteDados, isLoading: carregandoNomeCliente } = useBuscarPorUsuario(
    clienteIdParaExtrato ?? 0
  );
  const nomeCliente = clienteDados?.nome;

  const {
    linhasExtratoMovimentacoes,
    paginacaoExtratoCreditoCliente,
    carregandoExtrato,
    buscandoExtrato,
    erroExtrato,
    paginaAtualExtrato,
    itensPorPaginaExtrato,
    definirPaginaAtualExtrato,
    filtrosExtrato,
    definirFiltrosExtrato,
    limparFiltrosExtrato,
    opcoesProdutoFiltroExtrato,
    carregandoOpcoesProdutoExtrato,
  } = useExtratoMovimentacoesCreditoCliente({ clienteId: clienteIdParaExtrato });

  const linhasTabelaExtratoCliente = useMemo(
    () => linhasExtratoMovimentacoes.map(mapearLinhaExtratoParaTabela),
    [linhasExtratoMovimentacoes]
  );

  const paginaAtual = paginacaoExtratoCreditoCliente?.current_page ?? paginaAtualExtrato;
  const ultimaPagina = paginacaoExtratoCreditoCliente?.last_page ?? 1;
  const totalRegistrosExtrato = paginacaoExtratoCreditoCliente?.total ?? 0;

  const paginasNavegacaoExtrato = useMemo(
    () => gerarPaginasNavegacao(paginaAtual, ultimaPagina),
    [paginaAtual, ultimaPagina]
  );

  const extratoCarregando = carregandoExtrato || buscandoExtrato;

  const colunasExtratoMovimentacoes = useMemo(
    () => [
      { label: "Data", accessor: "dataHoraMovimentacao" },
      { label: "Operação", accessor: "tipoOperacaoDescricao", limit: 60 },
      {
        label: "Transação",
        accessor: "direcaoMovimentoDescricao",
        customRender: (valorDirecaoMovimento: string) => (
          <Badge
            className={
              valorDirecaoMovimento === "entrada"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }
          >
            {valorDirecaoMovimento === "entrada" ? "Entrada" : "Saída"}
          </Badge>
        ),
      },
      { label: "Qtd. créditos", accessor: "quantidadeCreditosFormatada" },
      { label: "Saldo após (produto)", accessor: "saldoAposMovimento" },
      { label: "Produto", accessor: "nomeProduto", limit: 40 },
      { label: "Remessa", accessor: "numeroRemessa" },
      { label: "Observação", accessor: "observacaoNegocio", limit: 60 },
    ],
    []
  );

  const onGoToPageExtrato = (page: number | "prev" | "next") => {
    if (page === "prev") {
      definirPaginaAtualExtrato(Math.max(1, paginaAtual - 1));
      return;
    }

    if (page === "next") {
      definirPaginaAtualExtrato(Math.min(ultimaPagina, paginaAtual + 1));
      return;
    }

    definirPaginaAtualExtrato(page);
  };

  if (!clienteIdParaExtrato) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        Não foi possível identificar o cliente. Acesse esta tela a partir da lista de clientes para
        visualizar o extrato de créditos.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-b border-neutral-200 pb-3">
        <Receipt className="h-5 w-5 text-neutral-700" />
        <h2 className="text-lg font-semibold text-neutral-900">
          Extrato de movimentações de créditos
        </h2>
      </div>

      <p className="text-sm font-medium text-neutral-800">
        Cliente:{" "}
        {carregandoNomeCliente ? (
          <span className="text-muted-foreground">Carregando...</span>
        ) : (
          <span className="font-semibold">{nomeCliente || "N/A"}</span>
        )}
      </p>

      <p className="text-sm text-muted-foreground">
        Histórico auditável de compras, consumos em remessas, estornos e demais operações de
        crédito.
      </p>

      {erroExtrato && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Não foi possível carregar o extrato. Tente novamente em instantes.
        </div>
      )}

      <DynamicTable
        title="Extrato de créditos"
        description="Movimentações registradas para auditoria financeira do cliente"
        isLoadingPage={extratoCarregando}
        data={linhasTabelaExtratoCliente}
        totalItems={totalRegistrosExtrato}
        itemsPerPage={itensPorPaginaExtrato}
        columns={colunasExtratoMovimentacoes}
        paginations={paginasNavegacaoExtrato}
        onGoToPage={onGoToPageExtrato}
        currentPage={paginaAtual}
        lastPage={ultimaPagina}
        childerSearch={
          <BarraFiltrosExtratoCredito
            filtros={filtrosExtrato}
            onAlterarFiltros={definirFiltrosExtrato}
            onLimparFiltros={limparFiltrosExtrato}
            opcoesProduto={opcoesProdutoFiltroExtrato}
            carregandoProdutos={carregandoOpcoesProdutoExtrato}
          />
        }
      />
    </div>
  );
}

export { ExtratoMovimentacoesCreditoCliente };
